import { useState, useEffect, useMemo, useRef } from "react";
import { Clock, UserCheck, DollarSign, Plus, Edit2, Trash2, X, Settings, Save, Upload, Download, Eye, ArrowLeftRight, Undo2, Calendar, FileText, CheckCircle, AlertTriangle, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import DatePicker from "@/components/ui/DatePicker";
import api, { errMsg, downloadFile } from "../lib/api";
import { todayIST, prevMonthIST, fmt12 } from "../lib/datetime";
import useAutoRefresh from "../lib/useAutoRefresh";

export default function AttendancePage() {
  const userRole = (() => {
    try {
      return (JSON.parse(localStorage.getItem("lss_user") || "{}").role || "owner").toLowerCase();
    } catch {
      return "owner";
    }
  })();
  const isOwner = userRole === "owner";
  const [staffList, setStaffList] = useState([]);
  const [payrollReport, setPayrollReport] = useState([]);
  const [month, setMonth] = useState(prevMonthIST());
  const [pageLoading, setPageLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [wiping, setWiping] = useState(false);
  const [exporting, setExporting] = useState(false);

  // Detail Modal (Eye Icon)
  const [selectedStaffDetail, setSelectedStaffDetail] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);

  // 3-Click Shift Swap Modal
  const [showSwapModal, setShowSwapModal] = useState(false);
  const [swapForm, setSwapForm] = useState({
    date: todayIST(),
    staff_a_id: "",
    staff_b_id: "",
    swap_type: "duty_cover",
    notes: "",
    proof_url: "",
    proof_name: "",
  });
  const [swapLoading, setSwapLoading] = useState(false);
  const swapProofRef = useRef(null);

  const handleSwapProofFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      toast.error("Proof file must be under 2 MB");
      if (swapProofRef.current) swapProofRef.current.value = "";
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      setSwapForm((prev) => ({ ...prev, proof_url: reader.result, proof_name: file.name }));
      toast.success(`Proof attached: ${file.name}`);
    };
    reader.onerror = () => toast.error("Could not read proof file");
    reader.readAsDataURL(file);
  };

  // Salary Manager Modal
  const [showSalaryModal, setShowSalaryModal] = useState(false);
  const [salaryRows, setSalaryRows] = useState([]);
  const [salarySaving, setSalarySaving] = useState(false);
  // Salary advances (Option C): recorded here, posted to Cash Book as pending,
  // deducted by the salary calc in the advance's month.
  const [advStaffId, setAdvStaffId] = useState("");
  const [advAmount, setAdvAmount] = useState("");
  const [advDate, setAdvDate] = useState(todayIST());
  const [advList, setAdvList] = useState([]);
  const [advSaving, setAdvSaving] = useState(false);

  const fileInputRef = useRef(null);

  useEffect(() => {
    setPageLoading(true);
    Promise.all([fetchStaff(), fetchPayrollCalculation(), fetchSwaps(), fetchLeaves(true), fetchRoster()]).finally(() => setPageLoading(false));
  }, [month]);

  const fetchStaff = async () => {
    try {
      const res = await api.get("/staff");
      setStaffList(res.data || []);
    } catch (e) {
      console.error(e);
    }
  };

  const fetchPayrollCalculation = async () => {
    try {
      const res = await api.get(`/salary/calculation?month=${month}`);
      setPayrollReport(res.data?.payroll || []);
    } catch (e) {
      console.error(e);
    }
  };

  const handleWipeMonth = async () => {
    if (!window.confirm(`Delete ALL attendance punch records for ${month}?\n\nRoster, swaps and leave requests are kept.\nThis cannot be undone.`)) {
      return;
    }
    setWiping(true);
    try {
      const res = await api.delete(`/attendance/wipe?month=${month}`);
      toast.success(`Wiped ${res.data?.deleted ?? 0} attendance records for ${month}`);
      fetchPayrollCalculation();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setWiping(false);
    }
  };

  // Salary sheet. Built on the server from the SAME calculate_monthly_salary()
  // call that fills the table above, so the file can never drift from the
  // statement the owner is looking at when they click it.
  const handleExportSalary = async () => {
    setExporting(true);
    try {
      const fname = await downloadFile(
        `/reports/payroll.xlsx?month=${month}`,
        `Geetanjali_Salary_Statement_${month}.xlsx`
      );
      toast.success(`Exported ${fname}`);
    } catch (e) {
      toast.error(e?.message || errMsg(e));
    } finally {
      setExporting(false);
    }
  };

  const handleBiometricUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const formData = new FormData();
    formData.append("file", file);
    try {
      const res = await api.post(`/attendance/upload-biometric?month=${month}`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 300000, // large biometric files need more than the default 25s API timeout
      });
      toast.success(res.data?.message || "Biometric attendance report uploaded & recalculated successfully!");
      if (res.data?.detected_month && res.data.detected_month !== month) {
        setMonth(res.data.detected_month);
        toast(`Showing ${res.data.detected_month} — the file's own period.`, { icon: "📅" });
      }
      fetchPayrollCalculation();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleToggleLateExcuse = async (attId) => {
    try {
      const res = await api.post(`/attendance/${attId}/toggle-excuse`, { reason: "Approved by Owner" });
      toast.success(res.data?.message || "Late excuse updated");
      fetchPayrollCalculation();
      if (selectedStaffDetail) {
        setSelectedStaffDetail((prev) => ({
          ...prev,
          daily_punches: prev.daily_punches.map((p) => p.id === attId ? { ...p, is_late_excused: !p.is_late_excused } : p)
        }));
      }
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  const handleSaveSwap = async (e) => {
    e.preventDefault();
    if (!swapForm.staff_a_id || !swapForm.staff_b_id) {
      toast.error("Please select both Staff A and Staff B");
      return;
    }
    setSwapLoading(true);
    try {
      await api.post("/salary/swap", swapForm);
      toast.success("Shift swap request submitted for approval");
      setShowSwapModal(false);
      setSwapForm({ date: todayIST(), staff_a_id: "", staff_b_id: "", swap_type: "duty_cover", notes: "", proof_url: "", proof_name: "" });
      if (swapProofRef.current) swapProofRef.current.value = "";
      fetchPayrollCalculation();
      fetchSwaps();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setSwapLoading(false);
    }
  };

  const handleSwapAction = async (id, action) => {
    try {
      await api.post(`/salary/swaps/${id}/approve`, { action });
      toast.success(`Swap ${action}`);
      fetchSwaps();
      fetchPayrollCalculation();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const handleOpenSalaryManager = async () => {
    setShowSalaryModal(true);
    try {
      const res = await api.get("/staff");
      const staff = (res.data || []).map((s) => ({
        id: s.id,
        name: s.name || "",
        department: s.department || s.role || "STYLIST",
        base_salary: s.base_salary || 25000,
        emp_code: s.emp_code || "",
        hire_date: (s.hire_date || "").slice(0, 10),
        exit_date: (s.exit_date || "").slice(0, 10),
      }));
      setSalaryRows(staff);
    } catch (err) {
      toast.error(errMsg(err));
    }
    fetchAdvances();
  };

  const fetchAdvances = async () => {
    try {
      const res = await api.get(`/salary/advances?month=${month}`);
      setAdvList(res.data || []);
    } catch (e) {
      console.error(e);
    }
  };

  const handleSaveAdvance = async (e) => {
    e.preventDefault();
    if (!advStaffId) return toast.error("Select staff");
    const amt = Number(advAmount) || 0;
    if (amt <= 0) return toast.error("Enter advance amount");
    if (!advDate) return toast.error("Pick advance date");
    setAdvSaving(true);
    try {
      const res = await api.post("/salary/advances", { staff_id: advStaffId, amount: amt, date: advDate });
      void res;
      toast.success(`Advance ₹${amt.toLocaleString()} recorded — approve it in Cash Book to move the cash`);
      setAdvAmount("");
      fetchAdvances();
      fetchPayrollCalculation();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setAdvSaving(false);
    }
  };

  const handleDeleteAdvance = async (id) => {
    if (!window.confirm("Delete this advance record?")) return;
    try {
      await api.delete(`/salary/advances/${id}`);
      toast.success("Advance deleted");
      fetchAdvances();
      fetchPayrollCalculation();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  const handleSaveAllSalaries = async () => {
    setSalarySaving(true);
    try {
      const res = await api.post("/staff/bulk-update", salaryRows);
      const saved = res.data?.saved ?? 0;
      const deleted = res.data?.deleted ?? 0;
      const marked = salaryRows.filter((r) => r._delete).length;
      if (marked > 0 && deleted === 0) {
        toast.error("Delete did not save — nothing was removed. Please retry.");
      } else {
        toast.success(`Saved ${saved} staff${deleted > 0 ? `, removed ${deleted}` : ""}`);
      }
      setShowSalaryModal(false);
      fetchStaff();
      fetchPayrollCalculation();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setSalarySaving(false);
    }
  };

  const openStaffEyeDetail = (staffRecord) => {
    setSelectedStaffDetail(staffRecord);
    setShowDetailModal(true);
  };

  // Roster Window (daily grid: staff x ONE date)
  //
  // Owner instruction (19 Oct 2026): the master roster is made day by day, not
  // week by week. Nothing in the data model had to move — the backend has
  // always stored one row per staff+date and recalculates that day's punch on
  // save — so this is a screen change only: one date is picked, each staff is
  // marked Working (with a shift) or Weekly Off for that date, and Save writes
  // just that date.
  const addDays = (dateStr, n) => {
    const d = new Date(dateStr + "T12:00:00");
    d.setDate(d.getDate() + n);
    return d.toISOString().slice(0, 10);
  };
  const WEEKDAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
  const weekdayIdx = (dateStr) => (new Date(dateStr + "T12:00:00").getDay() + 6) % 7;
  const prettyDate = (dateStr) => `${WEEKDAYS[weekdayIdx(dateStr)]}, ${dateStr.slice(8)}/${dateStr.slice(5, 7)}/${dateStr.slice(0, 4)}`;
  const [showRosterModal, setShowRosterModal] = useState(false);
  const [rosterDate, setRosterDate] = useState(() => todayIST());
  const [rosterRows, setRosterRows] = useState({}); // "staffId|date" -> { day: ""|"work"|"off", shift }
  const [rosterLoading, setRosterLoading] = useState(false);
  const [rosterSaving, setRosterSaving] = useState(false);
  const ROSTER_SHIFTS = ["Morning (10-8)", "Evening (11-9)"];
  const rosterKey = (staffId, date) => `${staffId}|${date}`;
  const daySlice = (staffId) => rosterRows[rosterKey(staffId, rosterDate)] || { day: "", shift: "" };
  const setDaySlice = (staffId, patch) =>
    setRosterRows((prev) => {
      const k = rosterKey(staffId, rosterDate);
      return { ...prev, [k]: { day: "", shift: "", ...(prev[k] || {}), ...patch } };
    });

  // `silent` is what keeps an open window still: the 30-second poll and the
  // refocus reload pass true, so the roster grid is repainted in place instead
  // of being swapped for "Loading roster..." every half minute. Only an
  // explicit open shows the spinner, exactly as Inventory's fetch does.
  const fetchRoster = async (silent = false) => {
    if (!silent) setRosterLoading(true);
    try {
      const res = await api.get("/salary/roster");
      const map = {}; // "staffId|date" -> { day, shift }
      for (const e of res.data?.roster_entries || []) {
        if (!e.staff_id || !e.date) continue;
        const off = e.is_weekly_off || e.shift_name === "Weekly Off";
        map[`${e.staff_id}|${e.date}`] = { day: off ? "off" : "work", shift: off ? "" : e.shift_name || "" };
      }
      // Merge server values under any unsaved local edits for the shown date
      const dt = rosterDate;
      setRosterRows((prev) => {
        const keep = Object.fromEntries(Object.entries(prev).filter(([k]) => k.endsWith(`|${dt}`)));
        return { ...map, ...keep };
      });
    } catch (e) {
      // A background refresh that fails must not pop a toast every 30 seconds
      // over a window the user is reading.
      if (!silent) toast.error(errMsg(e));
      else console.error(e);
    } finally {
      if (!silent) setRosterLoading(false);
    }
  };

  // Punches land, swaps get approved and leaves get granted while this page is
  // open — pull them all in on a poll and on refocus. `pageLoading` (set solely
  // by the first mount) gates the page skeleton and the roster and leave
  // fetches run silent, so a background refresh repaints the data in place and
  // never blanks a window the user has open.
  useAutoRefresh(
    () => Promise.all([
      fetchStaff(), fetchPayrollCalculation(), fetchSwaps(), fetchLeaves(true), fetchRoster(true),
    ]),
    30000
  );

  const handleOpenRoster = () => {
    setShowRosterModal(true);
    fetchRoster();
  };

  const handleSaveRoster = async () => {
    // One date only: the shown day. The endpoint upserts per staff+date and
    // recalculates that day's punch, so a daily save is the native shape.
    const entries = [];
    for (const [k, r] of Object.entries(rosterRows)) {
      if (!k.endsWith(`|${rosterDate}`)) continue; // only the shown day
      const staff_id = k.split("|")[0];
      if (!r.day) continue;
      entries.push({
        staff_id,
        date: rosterDate,
        shift_name: r.day === "off" ? "Weekly Off" : (r.shift || ROSTER_SHIFTS[0]),
      });
    }
    if (entries.length === 0) {
      toast.error("No roster rows set");
      return;
    }
    setRosterSaving(true);
    try {
      const res = await api.post("/salary/roster", { entries });
      toast.success(res.data?.message || "Roster saved");
      fetchRoster();
      fetchPayrollCalculation();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setRosterSaving(false);
    }
  };

  // Fill the shown day from the previous day's saved roster — the daily
  // equivalent of the old "Copy Last Week". It only paints the grid; nothing is
  // written until Save, so the owner can adjust a person and then commit.
  const handleFillPrevDay = () => {
    const prevDate = addDays(rosterDate, -1);
    const patch = {};
    let n = 0;
    for (const s of staffList) {
      const src = rosterRows[rosterKey(s.id, prevDate)];
      if (!src || !src.day) continue;
      patch[rosterKey(s.id, rosterDate)] = { day: src.day, shift: src.shift };
      n += 1;
    }
    if (n === 0) {
      toast.error(`No roster saved for ${prevDate} to copy`);
      return;
    }
    setRosterRows((prev) => ({ ...prev, ...patch }));
    toast.success(`Filled ${n} staff from ${prevDate} — review, then Save`);
  };

  // Leave Requests + Proof Attachment + Approval
  const [showLeaveModal, setShowLeaveModal] = useState(false);
  const [leaveList, setLeaveList] = useState([]);
  const [leaveLoading, setLeaveLoading] = useState(false);
  const [leaveSaving, setLeaveSaving] = useState(false);
  const [leaveForm, setLeaveForm] = useState({
    staff_id: "",
    date: todayIST(),
    reason: "",
    proof_url: "",
    proof_name: "",
  });
  const proofInputRef = useRef(null);

  // `silent` governs the spinner as well as the toast. The poll passes true,
  // so the Leave & Proof window repaints its list in place instead of swapping
  // to "Loading requests..." every half minute under someone reading it.
  const fetchLeaves = async (silent = false) => {
    if (!silent) setLeaveLoading(true);
    try {
      const res = await api.get("/salary/holiday-requests");
      setLeaveList(res.data?.holiday_requests || []);
    } catch (e) {
      if (!silent) toast.error(errMsg(e));
      else console.error(e);
    } finally {
      if (!silent) setLeaveLoading(false);
    }
  };

  const handleOpenLeaves = () => {
    setShowLeaveModal(true);
    fetchLeaves();
    fetchSwaps();
  };

  const handleProofFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      toast.error("Proof file must be under 2 MB");
      if (proofInputRef.current) proofInputRef.current.value = "";
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      setLeaveForm((prev) => ({ ...prev, proof_url: reader.result, proof_name: file.name }));
      toast.success(`Proof attached: ${file.name}`);
    };
    reader.onerror = () => toast.error("Could not read proof file");
    reader.readAsDataURL(file);
  };

  const handleSaveLeave = async (e) => {
    e.preventDefault();
    if (!leaveForm.staff_id || !leaveForm.date) {
      toast.error("Select staff and date");
      return;
    }
    setLeaveSaving(true);
    try {
      await api.post("/salary/holiday-request", {
        staff_id: leaveForm.staff_id,
        date: leaveForm.date,
        reason: leaveForm.reason,
        proof_url: leaveForm.proof_url,
      });
      toast.success("Leave request submitted for approval");
      setLeaveForm({ staff_id: "", date: todayIST(), reason: "", proof_url: "", proof_name: "" });
      if (proofInputRef.current) proofInputRef.current.value = "";
      fetchLeaves();
      fetchPayrollCalculation();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setLeaveSaving(false);
    }
  };

  const handleLeaveAction = async (id, action) => {
    try {
      await api.post(`/salary/holiday-requests/${id}/approve`, { action });
      toast.success(`Request ${action}`);
      fetchLeaves();
      fetchPayrollCalculation();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const pendingLeaves = leaveList.filter((l) => l.status === "pending" && (l.date || "").startsWith(month)).length;


  // Exceptions & Approvals main window (tabs like inventory)
  const [activeTab, setActiveTab] = useState("payroll"); // payroll | exceptions
  const [excFilter, setExcFilter] = useState("All");
  const [swapList, setSwapList] = useState([]);

  const fetchSwaps = async () => {
    try {
      const res = await api.get(`/salary/swaps?month=${month}`);
      setSwapList(res.data?.shift_swaps || []);
    } catch (e) {
      console.error(e);
    }
  };

  const pendingSwaps = swapList.filter((s) => (s.status || "pending") === "pending").length;

  const openExceptions = () => {
    setActiveTab("exceptions");
    fetchSwaps();
    if (leaveList.length === 0) fetchLeaves();
  };

  const monthLeaves = leaveList.filter((l) => (l.date || "").startsWith(month));
  const excEvents = useMemo(() => {
    const evts = [];
    for (const st of payrollReport) {
      for (const p of st.daily_punches || []) {
        if (p.status === "absent") {
          evts.push({ type: p.peak_penalty_applied ? "Peak Penalty" : "Absent", staff: st.staff_name, date: p.date, detail: p.peak_penalty_applied ? "Fri/Sat/Sun absent −1 day +1 extra peak day" : "Absent −1 day", status: p.status });
        }
        if (p.status === "half_day") {
          const hrs = Number(p.hours_worked || 0);
          const why = (p.late_minutes || 0) > 120 ? `In at ${fmt12(p.clock_in) || "--:--"} (>2h late)` : `${hrs}h worked (<8h)`;
          evts.push({ type: "Half Day", staff: st.staff_name, date: p.date, detail: `${why} −0.5 day`, status: p.status });
        }
        if (p.status === "short_day") {
          evts.push({ type: "Short Day", staff: st.staff_name, date: p.date, detail: `${fmt12(p.clock_in) || "--:--"} → ${fmt12(p.clock_out) || "--:--"} (${p.hours_worked || 0}h worked, no present day, hours to OT)`, status: p.status });
        }
        if (p.late_minutes > 15 && !p.is_late_excused) {
          evts.push({ type: "Late Mark", staff: st.staff_name, date: p.date, detail: `In at ${fmt12(p.clock_in) || "--:--"} (${p.late_minutes}m late, counts to 3-late rule)`, status: "late" });
        }
        if (p.late_minutes > 15 && p.is_late_excused) {
          evts.push({ type: "Waiver", staff: st.staff_name, date: p.date, detail: `Late ${p.late_minutes}m excused by owner — excluded from penalty`, status: "waived" });
        }
        if (p.notes && p.notes.includes("Shift swap cover")) {
          evts.push({ type: "Swap Cover", staff: st.staff_name, date: p.date, detail: p.notes, status: "swap" });
        }
      }
    }
    for (const s of swapList) {
      const st = s.status || "pending";
      evts.push({ type: st === "approved" ? "Swap Approved" : st === "rejected" ? "Swap Rejected" : "Swap Pending", staff: `${s.staff_a_name} → ${s.staff_b_name}`, date: s.date, detail: `${s.swap_type || "duty_cover"} — ${s.notes || "shift swap"}${s.proof_url ? " (proof attached)" : ""}${s.approved_by ? ` — by ${s.approved_by}` : ""}`, status: st });
    }
    for (const l of monthLeaves) {
      evts.push({ type: l.status === "approved" ? "Leave Approved" : l.status === "rejected" ? "Leave Rejected" : "Leave Pending", staff: l.staff_name, date: l.date, detail: `${l.reason || "leave"}${l.proof_url ? " (proof attached)" : ""}${l.approved_by ? ` — by ${l.approved_by}` : ""}`, status: l.status });
    }
    return evts.sort((a, b) => (a.date || "").localeCompare(b.date || ""));
  }, [payrollReport, swapList, monthLeaves]);

  const excCounts = useMemo(() => {
    const c = { All: excEvents.length };
    for (const e of excEvents) c[e.type] = (c[e.type] || 0) + 1;
    return c;
  }, [excEvents]);

  const filteredExc = excFilter === "All" ? excEvents : excEvents.filter((e) => e.type === excFilter);

  // Toolbar badge counts for all tabs
  // Staff carrying a roster row on the shown date (the button badge tracks the
  // day on screen, which defaults to today).
  const rosterDayCount = Object.entries(rosterRows).filter(([k, r]) => k.endsWith(`|${rosterDate}`) && r && r.day).length;

  return (
    <div className="p-6 space-y-6 max-w-7xl mx-auto">
      {/* Header Bar */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 bg-white p-6 rounded-2xl border border-slate-200 shadow-xs">
        <div>
          <h1 className="text-2xl font-extrabold text-slate-950 flex items-center gap-2">
            <UserCheck className="w-7 h-7 text-amber-700" />
            Attendance & Salary Engine
          </h1>
        </div>

        <DatePicker
          label="Month"
          type="month"
          value={month}
          onChange={(e) => setMonth(e.target.value)}
          testId="att-month"
        />
      </div>

      {/* Professional action toolbar — one uniform row (extra top pad so corner badges never clip) */}
      <div className="lss-card px-4 pt-4 pb-3">
        <div className="flex items-center gap-2 overflow-x-auto pt-1">
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleBiometricUpload}
            accept=".xlsx,.xls,.csv"
            className="hidden"
          />

          {isOwner && (
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              title="Import the biometric punch report for this month (payroll input)"
              className="lss-btn-gold px-4 py-2.5 text-xs font-bold uppercase tracking-wider flex items-center gap-2 shrink-0 disabled:opacity-50"
            >
              <Upload className="w-4 h-4" />
              {uploading ? "Uploading..." : "Upload Report"}
            </button>
          )}

          <span className="w-px h-7 bg-slate-200 shrink-0" />

          <button
            onClick={() => setShowSwapModal(true)}
            className="px-4 py-2.5 text-xs font-bold uppercase tracking-wider rounded-[10px] border border-slate-300 bg-white text-slate-800 hover:border-amber-400 hover:text-amber-800 hover:bg-amber-50/50 transition flex items-center gap-2 shrink-0"
          >
            <ArrowLeftRight className="w-4 h-4 text-emerald-700" />
            Shift Swap
            {swapList.length > 0 && (
              <span className="bg-emerald-600 text-white text-[10px] font-extrabold min-w-5 h-5 px-1.5 rounded-full flex items-center justify-center">
                {swapList.length}
              </span>
            )}
          </button>

          <button
            onClick={handleOpenRoster}
            className="px-4 py-2.5 text-xs font-bold uppercase tracking-wider rounded-[10px] border border-slate-300 bg-white text-slate-800 hover:border-amber-400 hover:text-amber-800 hover:bg-amber-50/50 transition flex items-center gap-2 shrink-0"
          >
            <Calendar className="w-4 h-4 text-emerald-700" />
            Daily Roster
            {rosterDayCount > 0 && (
              <span className="bg-emerald-600 text-white text-[10px] font-extrabold min-w-5 h-5 px-1.5 rounded-full flex items-center justify-center">
                {rosterDayCount}
              </span>
            )}
          </button>

          <button
            onClick={handleOpenLeaves}
            className="px-4 py-2.5 text-xs font-bold uppercase tracking-wider rounded-[10px] border border-slate-300 bg-white text-slate-800 hover:border-amber-400 hover:text-amber-800 hover:bg-amber-50/50 transition flex items-center gap-2 shrink-0"
          >
            <FileText className="w-4 h-4 text-sky-800" />
            Leave & Proof
            {pendingLeaves > 0 && (
              <span className="bg-rose-600 text-white text-[10px] font-extrabold min-w-5 h-5 px-1.5 rounded-full flex items-center justify-center">
                {pendingLeaves}
              </span>
            )}
          </button>

          <span className="w-px h-7 bg-slate-200 shrink-0" />

{isOwner && (
            <button
              onClick={handleOpenSalaryManager}
              className="px-4 py-2.5 text-xs font-bold uppercase tracking-wider rounded-[10px] border border-slate-300 bg-white text-slate-800 hover:border-amber-400 hover:text-amber-800 hover:bg-amber-50/50 transition flex items-center gap-2 shrink-0 relative"
            >
              <Settings className="w-4 h-4 text-amber-700" />
              Salary Master
              {staffList.length > 0 && (
                <span className="bg-slate-950 text-amber-300 text-[10px] font-extrabold min-w-5 h-5 px-1.5 rounded-full flex items-center justify-center">
                  {staffList.length}
                </span>
              )}
            </button>
          )}

          {isOwner && (
            <button
              onClick={handleWipeMonth}
              disabled={wiping}
              title={`Delete all attendance records for ${month}`}
              className="px-4 py-2.5 text-xs font-bold uppercase tracking-wider rounded-[10px] border transition flex items-center gap-2 shrink-0 disabled:opacity-50 bg-white text-rose-700 border-rose-300 hover:bg-rose-50 hover:border-rose-400"
            >
              <Trash2 className="w-4 h-4" />
              {wiping ? "Wiping..." : "Wipe Month"}
            </button>
          )}
        </div>
      </div>

      {/* Section Tabs — like inventory window */}
      <div className="lss-card px-4 py-3 flex items-center gap-2 overflow-x-auto">
        {[
          { id: "payroll", label: "Payroll Statement", icon: FileText, count: payrollReport.length },
          { id: "exceptions", label: "Exceptions & Approvals", icon: AlertTriangle, count: excEvents.length },
        ].map((sec) => {
          const Icon = sec.icon;
          const isActive = activeTab === sec.id;
          return (
            <button
              key={sec.id}
              onClick={() => (sec.id === "exceptions" ? openExceptions() : setActiveTab("payroll"))}
              className={`relative px-4 py-2.5 text-xs font-extrabold uppercase tracking-wider rounded-xl transition-all flex items-center gap-2 shrink-0 ${
                isActive
                  ? "bg-slate-950 text-amber-300 shadow-md shadow-slate-950/20"
                  : "text-slate-700 hover:text-slate-950 hover:bg-slate-100"
              }`}
            >
              <Icon className={`w-4 h-4 ${isActive ? "text-amber-400" : "text-slate-500"}`} />
              <span>{sec.label}</span>
              {sec.count > 0 && (
                <span className={`text-[10px] font-extrabold px-1.5 py-0.5 rounded-full ${isActive ? "bg-amber-400 text-slate-950" : "bg-slate-200 text-slate-700"}`}>
                  {sec.count}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Main Reconciliation & Payroll Summary Table */}
      {activeTab === "payroll" && (
      <div className="lss-card overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between gap-3 bg-slate-50/70 sticky top-0 z-10">
          <h2 className="text-xs font-bold uppercase tracking-wider text-slate-700 flex items-center gap-2">
            <FileText className="w-4 h-4 text-amber-700" />
            Monthly Attendance & Payroll Statement ({month}) — {payrollReport.length} Staff
          </h2>
          <div className="flex items-center gap-2 shrink-0">
            <span className="text-xs font-bold text-slate-600 bg-white px-3 py-1 rounded-full border border-slate-200">
              {payrollReport.length} Active Staff Members
            </span>
            <button
              onClick={handleExportSalary}
              disabled={exporting || payrollReport.length === 0}
              data-testid="export-salary"
              title={payrollReport.length === 0
                ? `No payroll rows for ${month} yet`
                : `Download the ${month} salary sheet (2 tabs: statement + daily attendance)`}
              className="lss-btn-gold px-4 py-2 text-xs uppercase tracking-wider font-bold flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Download className={`w-4 h-4 ${exporting ? "animate-pulse" : ""}`} />
              {exporting ? "Exporting..." : "Export Salary"}
            </button>
          </div>
        </div>

        {pageLoading ? (
          <div className="p-12 text-center text-slate-500 font-medium">Loading recalculated payroll metrics...</div>
        ) : payrollReport.length === 0 ? (
          <div className="p-12 text-center text-slate-500 font-medium">
            No attendance data found for {month}. Upload your biometric Excel file above to begin reconciliation.
          </div>
        ) : (
          <div className="overflow-auto max-h-[560px]">
            <table className="lss-table payroll-grid">
              <thead>
                <tr>
                  <th>Staff Member</th>
                  <th className="text-right">Base Salary</th>
                  <th className="text-center">Presents</th>
                  <th className="text-center">Half Days</th>
                  <th className="text-center">Absent</th>
                  <th className="text-center">Weekly Offs</th>
                  <th className="text-center">Leaves</th>
                  <th className="text-center">Late Marks (&gt;15m)</th>
                  <th className="text-center">Late Penalty</th>
                  <th className="text-center">Peak Penalty</th>
                  <th className="text-center">OT Hours</th>
                  <th className="text-right">OT Pay</th>
                  <th className="text-center">Paid Days</th>
                  <th className="text-right">Net Payable Salary</th>
                  <th className="text-center sticky right-0 bg-white shadow-[-8px_0_12px_-8px_rgba(0,0,0,0.15)]">View</th>
                </tr>
              </thead>
              <tbody>
                {payrollReport.map((st) => (
                  <tr key={st.staff_id} className="hover:bg-slate-50/80 transition-colors">
                    <td className="font-bold text-slate-950">
                      <div>{st.staff_name}</div>
                      <div className="text-[10px] text-slate-500 font-semibold uppercase">{st.role || "Staff"}</div>
                    </td>
                    <td className="text-right tabular font-semibold text-slate-800">₹{st.base_salary?.toLocaleString()}</td>
                    <td className="text-center">
                      <span className="lss-badge text-[10px] bg-emerald-100 text-emerald-900 border-emerald-300">
                        {st.days_present} Days
                      </span>
                    </td>
                    <td className="text-center">
                      {(st.half_days || 0) > 0 ? (
                        <span className="lss-badge text-[10px] bg-amber-100 text-amber-950 border-amber-300" title="Each half day counts 0.5 paid day">
                          {st.half_days} Days
                        </span>
                      ) : (
                        <span className="text-slate-400">0</span>
                      )}
                    </td>
                    <td className="text-center">
                      {(st.absent_days || 0) > 0 ? (
                        <span className="lss-badge text-[10px] bg-rose-100 text-rose-900 border-rose-300" title={`Absent on: ${(st.absent_dates || []).join(", ") || "—"}`}>
                          {st.absent_days} Days
                        </span>
                      ) : (
                        <span className="text-slate-400">0</span>
                      )}
                    </td>
                    <td className="text-center text-slate-500 font-semibold">{st.weekly_offs} WO</td>
                    <td className="text-center">
                      {(st.approved_leaves || 0) > 0 ? (
                        <span className="lss-badge text-[10px] bg-emerald-600 text-white border-emerald-700" title="Owner-approved paid leaves">
                          {st.approved_leaves} LV
                        </span>
                      ) : (
                        <span className="text-slate-400">0</span>
                      )}
                    </td>
                    <td className="text-center tabular">
                      {st.unexcused_late_count > 0 ? (
                        <span className="text-amber-800 font-bold" title={`Late arrivals: ${(st.late_dates || []).join(", ") || "—"}`}>
                          {st.unexcused_late_count} Days
                        </span>
                      ) : (
                        <span className="text-slate-400">0</span>
                      )}
                    </td>
                    <td className="text-center text-rose-700 tabular font-semibold" title={st.late_penalty_days > 0 ? `Deducted for ${(st.late_dates || []).join(", ") || "late marks"} (3 lates = 0.5 day)` : ""}>
                      {st.late_penalty_days > 0 ? `-${st.late_penalty_days} Day` : "0"}
                    </td>
                    <td className="text-center text-rose-700 tabular font-semibold" title={st.peak_penalty_days > 0 ? `Deducted for Fri/Sat/Sun absent: ${(st.peak_dates || []).join(", ") || "—"}` : ""}>
                      {st.peak_penalty_days > 0 ? `-${st.peak_penalty_days} Day` : "0"}
                    </td>
                    <td className="text-center">
                      <span className="lss-badge text-[10px] bg-sky-100 text-sky-950 border-sky-300">
                        +{st.overtime_hours} hrs
                      </span>
                    </td>
                    <td className="text-right tabular font-semibold text-sky-900">₹{st.ot_pay?.toLocaleString()}</td>
                    <td className="text-center font-bold text-amber-800" title={`Present ${st.days_present} (incl. ${st.half_days || 0} half) − Half ${(st.half_days || 0) * 0.5} + Off ${st.weekly_offs || 0} + Leave ${st.approved_leaves || 0} − Late ${st.late_penalty_days || 0} − Peak ${st.peak_penalty_days || 0} − Absent ${st.absent_days || 0}`}>{st.effective_paid_days} Days</td>
                    <td className="text-right font-extrabold text-slate-950 tabular text-sm">
                      ₹{(st.net_payable ?? st.total_payable_salary)?.toLocaleString()}
                      {(st.advance_deducted || 0) > 0 && (
                        <span className="block text-[10px] font-bold text-amber-800" title="Advance deducted this month">
                          −₹{st.advance_deducted?.toLocaleString()} adv
                        </span>
                      )}
                    </td>
                    <td className="text-center sticky right-0 bg-white shadow-[-8px_0_12px_-8px_rgba(0,0,0,0.15)]">
                      <button
                        onClick={() => openStaffEyeDetail(st)}
                        className="p-1.5 text-amber-700 hover:text-amber-900 hover:bg-amber-100 rounded-lg transition border border-transparent hover:border-amber-300"
                        title="View Day-by-Day Punch Grid & Incentives"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      )}

      {/* Exceptions & Approvals main window */}
      {activeTab === "exceptions" && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-3">
            {["All", "Absent", "Late Mark", "Half Day", "Short Day", "Peak Penalty", "Waiver", "Swap Pending", "Swap Approved", "Leave Approved"].map((t) => (
              <button
                key={t}
                onClick={() => setExcFilter(t)}
                className={`lss-card p-3 text-left transition-all ${excFilter === t ? "ring-2 ring-amber-500 border-amber-400" : "hover:border-slate-300"}`}
              >
                <span className="lss-overline block truncate">{t}</span>
                <span className="text-2xl font-extrabold text-slate-950 tabular">{excCounts[t] || 0}</span>
              </button>
            ))}
          </div>

          <div className="flex flex-wrap gap-2">
            {["All", "Absent", "Late Mark", "Half Day", "Short Day", "Peak Penalty", "Waiver", "Swap Cover", "Swap Pending", "Swap Approved", "Leave Approved", "Leave Pending", "Leave Rejected", ...Object.keys(excCounts).filter((k) => !["All", "Absent", "Late Mark", "Half Day", "Short Day", "Peak Penalty", "Waiver", "Swap Cover", "Swap Pending", "Swap Approved", "Leave Approved", "Leave Pending", "Leave Rejected"].includes(k))].map((t) => (
              <button
                key={t}
                onClick={() => setExcFilter(t)}
                className={`px-3 py-1.5 text-[11px] font-bold uppercase tracking-wider rounded-full border transition ${
                  excFilter === t
                    ? "bg-slate-950 text-amber-300 border-slate-950"
                    : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"
                }`}
              >
                {t} ({excCounts[t] || 0})
              </button>
            ))}
          </div>

          <div className="lss-card overflow-hidden">
            <div className="px-6 py-4 border-b border-slate-200 bg-slate-50/70 flex items-center justify-between">
              <span className="text-xs font-bold uppercase tracking-wider text-slate-700">
                {excFilter === "All" ? "All Exceptions" : excFilter} ({filteredExc.length})
              </span>
              <span className="text-xs text-slate-500 font-semibold">Swaps · Absents · Lates · Waivers · Peaks · Leaves — {month}</span>
            </div>
            <div className="overflow-x-auto max-h-[520px] overflow-y-auto">
              <table className="lss-table min-w-[720px]">
                <thead>
                  <tr>
                    <th>Type</th>
                    <th>Staff</th>
                    <th>Date</th>
                    <th>Detail / Reason</th>
                    <th className="text-center">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredExc.map((e, i) => (
                    <tr key={i} className="hover:bg-slate-50/80">
                      <td>
                        <span className={`lss-badge text-[10px] ${
                          e.type === "Absent" ? "bg-rose-100 text-rose-900 border-rose-300"
                          : e.type === "Peak Penalty" ? "bg-rose-600 text-white border-rose-700"
                          : e.type === "Late Mark" ? "bg-amber-100 text-amber-950 border-amber-300"
                          : e.type === "Half Day" ? "bg-orange-100 text-orange-950 border-orange-300"
                          : e.type === "Short Day" ? "bg-yellow-100 text-yellow-950 border-yellow-300"
                          : e.type === "Waiver" ? "bg-emerald-100 text-emerald-900 border-emerald-300"
                          : e.type === "Leave Approved" ? "bg-emerald-600 text-white border-emerald-700"
                          : e.type === "Leave Rejected" ? "bg-slate-200 text-slate-700 border-slate-300"
                          : e.type === "Leave Pending" ? "bg-amber-500 text-slate-950 border-amber-600"
                          : "bg-sky-100 text-sky-950 border-sky-300"
                        }`}>
                          {e.type}
                        </span>
                      </td>
                      <td className="font-bold text-slate-950 text-xs">{e.staff}</td>
                      <td className="tabular text-xs font-semibold">{e.date}</td>
                      <td className="text-xs text-slate-600">{e.detail}</td>
                      <td className="text-center text-xs font-semibold text-slate-500">{e.status}</td>
                    </tr>
                  ))}
                  {filteredExc.length === 0 && (
                    <tr>
                      <td colSpan={5} className="text-center text-slate-500 font-medium py-8 text-xs">
                        No records in this category for {month}.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* 👁️ Eye Icon Staff Punch Grid Detail Modal */}
      {showDetailModal && selectedStaffDetail && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/60 backdrop-blur-xs" onClick={() => setShowDetailModal(false)}>
          <div className="bg-white border border-slate-200 rounded-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between bg-slate-50/70">
              <div>
                <h3 className="text-base font-extrabold text-slate-950 flex items-center gap-2">
                  <UserCheck className="w-5 h-5 text-amber-700" />
                  {selectedStaffDetail.staff_name} — Monthly Punch Grid & Audit
                </h3>
                <p className="text-xs text-slate-500 mt-1 font-medium">
                  Base Salary: ₹{selectedStaffDetail.base_salary?.toLocaleString()} • Per Day: ₹{selectedStaffDetail.per_day_rate} • OT Hours: {selectedStaffDetail.overtime_hours}h
                </p>
              </div>
              <button onClick={() => setShowDetailModal(false)} className="text-slate-400 hover:text-slate-700 p-2 transition">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 overflow-y-auto space-y-4 flex-1">
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                <div className="bg-slate-50 p-4 rounded-xl border border-slate-200">
                  <div className="lss-overline">Effective Paid Days</div>
                  <div className="text-xl font-extrabold text-amber-800 mt-1 tabular">{selectedStaffDetail.effective_paid_days} Days</div>
                </div>
                <div className="bg-slate-50 p-4 rounded-xl border border-slate-200">
                  <div className="lss-overline">Earned Base Salary</div>
                  <div className="text-xl font-extrabold text-slate-950 mt-1 tabular">₹{selectedStaffDetail.earned_base_salary?.toLocaleString()}</div>
                </div>
                <div className="bg-slate-50 p-4 rounded-xl border border-slate-200">
                  <div className="lss-overline">Total OT Pay</div>
                  <div className="text-xl font-extrabold text-sky-800 mt-1 tabular">₹{selectedStaffDetail.ot_pay?.toLocaleString()}</div>
                </div>
                <div className="bg-slate-50 p-4 rounded-xl border border-slate-200">
                  {/* net_payable, not total_payable_salary: the gross still has
                      the month's advance sitting in it, so this card used to
                      show ₹2,000-₹3,000 MORE than the staff table beside it and
                      more than the payroll statement it is meant to agree with. */}
                  <div className="lss-overline">Net Payable Amount</div>
                  <div className="text-xl font-extrabold text-emerald-800 mt-1 tabular">₹{(selectedStaffDetail.net_payable ?? selectedStaffDetail.total_payable_salary)?.toLocaleString()}</div>
                </div>
                <div className="bg-slate-50 p-4 rounded-xl border border-slate-200">
                  <div className="lss-overline">Joining Date</div>
                  <div className="text-xl font-extrabold text-slate-950 mt-1 tabular">{selectedStaffDetail.joining_date || "—"}</div>
                </div>
                <div className="bg-slate-50 p-4 rounded-xl border border-slate-200">
                  <div className="lss-overline">Exit Date</div>
                  <div className="text-xl font-extrabold text-slate-950 mt-1 tabular">{selectedStaffDetail.exit_date || "—"}</div>
                </div>
              </div>

              <div className="bg-white border border-slate-200 rounded-xl px-4 py-3">
                <div className="lss-overline mb-2">Days In — Attendance Breakdown</div>
                <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-xs font-bold">
                  <span className="text-emerald-800 tabular">● {selectedStaffDetail.days_present ?? 0} Present</span>
                  <span className="text-slate-500 tabular">● {(selectedStaffDetail.daily_punches || []).filter((p) => p.status === "present").length} Full Days</span>
                  <span className="text-amber-800 tabular">● {selectedStaffDetail.half_days ?? 0} Half Days</span>
                  <span className="text-yellow-800 tabular">● {(selectedStaffDetail.short_dates || []).length} Short Days</span>
                  <span className="text-rose-700 tabular">● {selectedStaffDetail.absent_days ?? 0} Absent</span>
                  <span className="text-slate-600 tabular">● {selectedStaffDetail.weekly_offs ?? 0} Week Offs</span>
                  <span className="text-emerald-700 tabular">● {selectedStaffDetail.approved_leaves ?? 0} Leaves</span>
                  <span className="text-slate-400 tabular">● {selectedStaffDetail.unexcused_late_count ?? 0} Late Marks</span>
                </div>
              </div>

              <h4 className="text-xs font-bold uppercase tracking-wider text-slate-700 mt-6">
                Day-by-Day Punch Times & Checks ({(selectedStaffDetail.daily_punches || []).length} Days — {month})
              </h4>
              <div className="border border-slate-200 rounded-xl overflow-auto max-h-[420px]">
                <table className="lss-table min-w-[760px]">
                  <thead>
                    <tr>
                      <th>Day</th>
                      <th>Date</th>
                      <th>Shift</th>
                      <th>Clock In</th>
                      <th>Clock Out</th>
                      <th className="text-center">Late Mins</th>
                      <th className="text-center">OT Hrs</th>
                      <th className="text-center">Status</th>
                      <th className="text-center">Owner Waiver</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedStaffDetail.daily_punches?.map((p) => {
                      const hasIn = p.clock_in && p.clock_in !== "00:00" && p.clock_in !== "--:--";
                      const hasOut = p.clock_out && p.clock_out !== "00:00" && p.clock_out !== "--:--";
                      const singlePunch = (hasIn ? 1 : 0) + (hasOut ? 1 : 0) === 1;
                      return (
                      <tr key={p.id} className={singlePunch ? "bg-amber-50/70 hover:bg-amber-50" : "hover:bg-slate-50/80"}>
                        <td className="tabular font-bold text-slate-700 text-xs">
                          {(() => {
                            const d = new Date((p.date || "") + "T12:00:00");
                            return isNaN(d) ? "—" : d.toLocaleDateString("en-IN", { weekday: "short" });
                          })()}
                        </td>
                        <td className="tabular font-bold text-slate-950">{p.date}</td>
                        <td className="text-slate-500 font-medium">
                          {(() => {
                            const isOff = p.shift_assigned === "Weekly Off" || p.is_roster_weekoff;
                            const hasIn = p.clock_in && p.clock_in !== "00:00";
                            const changeShift = async (e) => {
                              if (!e.target.value) return;
                              try {
                                const res = await api.post(`/attendance/${p.id}/set-shift`, { shift_name: e.target.value });
                                toast.success(res.data?.message || "Shift updated & recalculated");
                                fetchPayrollCalculation();
                                setSelectedStaffDetail((prev) => {
                                  if (!prev) return prev;
                                  const upd = res.data?.attendance;
                                  return { ...prev, daily_punches: (prev.daily_punches || []).map((x) => (x.id === p.id && upd ? { ...x, ...upd, is_roster_weekoff: x.is_roster_weekoff || upd.shift_assigned === "Weekly Off" } : x)) };
                                });
                              } catch (err) {
                                toast.error(errMsg(err));
                              }
                            };
                            return (
                              <div className="flex flex-col items-start gap-1">
                                {isOff && (
                                  <span className="lss-badge text-[10px] bg-rose-50 text-rose-900 border-rose-300">Weekly Off</span>
                                )}
                                {isOff && p.shift_assigned !== "Weekly Off" && (
                                  <span className="text-xs font-bold text-slate-800">{p.shift_assigned}</span>
                                )}
                                {!isOff && (
                                  <span>
                                    {p.shift_assigned}
                                    {p.is_roster_weekoff && (
                                      <span className="text-[10px] font-bold text-rose-700"> (Week Off)</span>
                                    )}
                                  </span>
                                )}
                                {isOff && hasIn && p.status === "present" && (
                                  <select
                                    value=""
                                    onChange={changeShift}
                                    className="text-[11px] font-bold px-1.5 py-1 rounded-lg border border-amber-300 bg-amber-50 text-amber-900 focus:outline-none focus:ring-2 focus:ring-amber-400/30 focus:border-amber-400 cursor-pointer max-w-[130px]"
                                    title="Change the shift for this day — late, OT & status recalculate"
                                  >
                                    <option value="">Set shift...</option>
                                    <option value="Morning (10-8)">Morning (10-8)</option>
                                    <option value="Evening (11-9)">Evening (11-9)</option>
                                    <option value="Weekly Off">Weekly Off</option>
                                  </select>
                                )}
                              </div>
                            );
                          })()}
                        </td>
                        <td className="tabular font-semibold">{(p.status === "absent" || ((p.shift_assigned === "Weekly Off" || p.is_roster_weekoff) && (!p.clock_in || p.clock_in === "00:00") && (!p.clock_out || p.clock_out === "00:00"))) ? "—" : (fmt12(p.clock_in) || "—")}</td>
                        <td className="tabular font-semibold">{(p.status === "absent" || ((p.shift_assigned === "Weekly Off" || p.is_roster_weekoff) && (!p.clock_in || p.clock_in === "00:00") && (!p.clock_out || p.clock_out === "00:00"))) ? "—" : (fmt12(p.clock_out) || "—")}</td>
                        <td className="text-center">
                          {p.late_minutes > 15 ? (
                            <span className="text-amber-800 font-bold">{p.late_minutes}m</span>
                          ) : (
                            <span className="text-slate-400">0</span>
                          )}
                        </td>
                        <td className="text-center font-bold text-sky-800">{p.overtime_hours > 0 ? `+${p.overtime_hours}h` : "-"}</td>
                        <td className="text-center uppercase">
                          {p.is_approved_leave && <span className="lss-badge text-[10px] bg-emerald-600 text-white border-emerald-700 block mb-1">Approved Leave</span>}
                          {p.status === "present" && <span className="lss-badge text-[10px] bg-emerald-100 text-emerald-900 border-emerald-300">Present</span>}
                          {p.status === "half_day" && <span className="lss-badge text-[10px] bg-amber-100 text-amber-950 border-amber-300">Half Day</span>}
                          {p.status === "short_day" && <span className="lss-badge text-[10px] bg-orange-100 text-orange-950 border-orange-300">Short Day</span>}
                          {p.status === "absent" && (
                            <span className="inline-flex flex-col items-center gap-1">
                              <span className="lss-badge text-[10px] bg-rose-100 text-rose-900 border-rose-300">Absent</span>
                              {p.peak_penalty_applied && (
                              <button
                                onClick={() => {
                                  setLeaveForm((prev) => ({ ...prev, staff_id: selectedStaffDetail?.staff_id || "", date: p.date || "" }));
                                  setShowLeaveModal(true);
                                  fetchLeaves();
                                }}
                                className="text-[10px] font-bold text-sky-800 hover:text-sky-950 underline"
                                title="Submit leave request with proof for this peak-penalty day"
                              >
                                + Leave & Proof
                              </button>
                              )}
                            </span>
                          )}
                          {p.status === "leave" && <span className="lss-badge text-[10px] bg-sky-100 text-sky-950 border-sky-300">Leave / WO</span>}
                          {singlePunch && <span className="block text-[10px] font-bold text-amber-800 normal-case mt-0.5">⚠ single punch</span>}
                        </td>
                        <td className="text-center">
                          {p.late_minutes > 15 ? (
                            isOwner ? (
                              <button
                                onClick={() => handleToggleLateExcuse(p.id)}
                                className={`px-3 py-1 rounded-full text-xs font-bold transition border ${
                                  p.is_late_excused
                                    ? "bg-emerald-100 text-emerald-900 border-emerald-300"
                                    : "bg-white hover:bg-amber-50 text-slate-500 hover:text-amber-800 border-slate-200 hover:border-amber-300"
                                }`}
                              >
                                {p.is_late_excused ? "✓ Excused (Owner)" : "Approve Waiver"}
                              </button>
                            ) : (
                              <span className={`text-xs font-bold ${p.is_late_excused ? "text-emerald-800" : "text-slate-400"}`}>
                                {p.is_late_excused ? "✓ Excused" : "Needs owner waiver"}
                              </span>
                            )
                          ) : (
                            <span className="text-slate-300">--</span>
                          )}
                        </td>
                      </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="px-6 py-4 border-t border-slate-200 flex justify-end bg-slate-50/70">
              <button onClick={() => setShowDetailModal(false)} className="lss-btn-outline px-5 py-2 text-sm">
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 3-Click Shift Swap Requests + Proof Approval */}
      {showSwapModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/60 backdrop-blur-xs" onClick={() => setShowSwapModal(false)}>
          <div className="bg-white border border-slate-200 rounded-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between bg-slate-50/70">
              <div>
                <h3 className="text-base font-extrabold text-slate-950 flex items-center gap-2">
                  <ArrowLeftRight className="w-5 h-5 text-emerald-700" />
                  Shift Swap Requests
                  {pendingSwaps > 0 && (
                    <span className="text-[10px] font-extrabold bg-rose-100 text-rose-900 border border-rose-300 px-2 py-0.5 rounded-full">
                      {pendingSwaps} Pending
                    </span>
                  )}
                </h3>
                <p className="text-xs text-slate-500 mt-1 font-medium">
                  Raise a request here — approval happens in the Leave Requests & Proof window.
                </p>
              </div>
              <button onClick={() => setShowSwapModal(false)} className="text-slate-400 hover:text-slate-700 transition">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 overflow-y-auto space-y-6 flex-1">
              <form onSubmit={handleSaveSwap} className="bg-slate-50 border border-slate-200 rounded-xl p-4">
                <h4 className="text-xs font-bold uppercase tracking-wider text-slate-700 mb-3">New Swap Request</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-bold text-slate-600 mb-1">1. Date of Swap</label>
                    <input
                      type="date"
                      value={swapForm.date}
                      onChange={(e) => setSwapForm({ ...swapForm, date: e.target.value })}
                      className="lss-input w-full px-3 py-2 text-xs"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-slate-600 mb-1">Proof (≤2 MB)</label>
                    <input type="file" ref={swapProofRef} onChange={handleSwapProofFile} accept="image/*,.pdf" className="hidden" />
                    <button
                      type="button"
                      onClick={() => swapProofRef.current?.click()}
                      className={`w-full px-3 py-2 text-xs font-bold rounded-[10px] border transition ${
                        swapForm.proof_url
                          ? "bg-emerald-50 text-emerald-900 border-emerald-300"
                          : "bg-white text-slate-600 border-slate-300"
                      }`}
                    >
                      {swapForm.proof_name ? `✓ ${swapForm.proof_name.slice(0, 18)}` : "Attach Proof"}
                    </button>
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-slate-600 mb-1">2. Person A (Taking Off)</label>
                    <select
                      value={swapForm.staff_a_id}
                      onChange={(e) => setSwapForm({ ...swapForm, staff_a_id: e.target.value })}
                      className="lss-input w-full px-3 py-2 text-xs"
                      required
                    >
                      <option value="">Select Staff A...</option>
                      {staffList.map((s) => (
                        <option key={s.id} value={s.id}>{s.name} ({s.role})</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-slate-600 mb-1">3. Person B (Covering Shift)</label>
                    <select
                      value={swapForm.staff_b_id}
                      onChange={(e) => setSwapForm({ ...swapForm, staff_b_id: e.target.value })}
                      className="lss-input w-full px-3 py-2 text-xs"
                      required
                    >
                      <option value="">Select Staff B...</option>
                      {staffList.filter(s => s.id !== swapForm.staff_a_id).map((s) => (
                        <option key={s.id} value={s.id}>{s.name} ({s.role})</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="flex justify-end gap-3 mt-3">
                  <button type="button" onClick={() => setShowSwapModal(false)} className="lss-btn-outline px-4 py-2 text-xs">
                    Cancel
                  </button>
                  <button type="submit" disabled={swapLoading} className="lss-btn-gold px-5 py-2 text-xs disabled:opacity-50">
                    {swapLoading ? "Submitting..." : "Submit for Approval ✓"}
                  </button>
                </div>
              </form>

              <div>
                <h4 className="text-xs font-bold uppercase tracking-wider text-slate-700 mb-3">
                  All Requests ({swapList.length})
                </h4>
                {swapList.length === 0 ? (
                  <div className="p-8 text-center text-slate-500 font-medium text-xs border border-dashed border-slate-300 rounded-xl">
                    No swap requests this month.
                  </div>
                ) : (
                  <div className="border border-slate-200 rounded-xl overflow-x-auto">
                    <table className="lss-table min-w-[700px]">
                      <thead>
                        <tr>
                          <th>Swap</th>
                          <th>Date</th>
                          <th className="text-center">Proof</th>
                          <th className="text-center">Status</th>
                          <th className="text-center">Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {swapList.map((s) => {
                          const st = s.status || "pending";
                          return (
                            <tr key={s.id} className="hover:bg-slate-50/80">
                              <td className="font-bold text-slate-950 text-xs">{s.staff_a_name} → {s.staff_b_name}</td>
                              <td className="tabular text-xs font-semibold">{s.date}</td>
                              <td className="text-center">
                                {s.proof_url ? (
                                  <a href={s.proof_url} target="_blank" rel="noreferrer" className="text-[11px] font-bold text-sky-800 hover:text-sky-950 underline">
                                    View Proof
                                  </a>
                                ) : (
                                  <span className="text-slate-300 text-xs">—</span>
                                )}
                              </td>
                              <td className="text-center">
                                {st === "approved" && <span className="lss-badge text-[10px] bg-emerald-100 text-emerald-900 border-emerald-300">Approved</span>}
                                {st === "rejected" && <span className="lss-badge text-[10px] bg-rose-100 text-rose-900 border-rose-300">Rejected</span>}
                                {st === "pending" && <span className="lss-badge text-[10px] bg-amber-100 text-amber-950 border-amber-300">Pending</span>}
                              </td>
                              <td className="text-center">
                                {st === "pending" ? (
                                  <span className="text-[11px] text-slate-400 font-medium">Awaiting approval</span>
                                ) : (
                                  <span className="text-[11px] text-slate-400 font-medium">by {s.approved_by || "—"}</span>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Salary Master Modal (owner only) */}
      {showSalaryModal && isOwner && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/60 backdrop-blur-xs" onClick={() => setShowSalaryModal(false)}>
          <div className="bg-white border border-slate-200 rounded-2xl w-full max-w-5xl p-6 space-y-4 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <h3 className="text-base font-extrabold text-slate-950 flex items-center gap-2">
                <Settings className="w-5 h-5 text-amber-700" />
                Staff Salary Master Settings
              </h3>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setSalaryRows([...salaryRows, { id: `new_${Date.now()}`, _new: true, _editing: true, name: "", department: "STYLIST", base_salary: 25000, emp_code: "", hire_date: "", exit_date: "" }])}
                  className="lss-btn-outline px-3 py-1.5 text-xs flex items-center gap-1.5"
                >
                  <Plus className="w-3.5 h-3.5" /> Add Employee
                </button>
                <button onClick={() => setShowSalaryModal(false)} className="text-slate-400 hover:text-slate-700 transition">
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            <div className="max-h-96 overflow-y-auto border border-slate-200 rounded-xl">
              <table className="lss-table">
                <thead>
                  <tr>
                    <th>Staff Name</th>
                    <th>Emp ID</th>
                    <th>Role / Dept</th>
                    <th className="text-right">Base Monthly Salary (₹)</th>
                    <th>Joining</th>
                    <th>Discontinue</th>
                    <th className="text-center">Edit / Delete</th>
                  </tr>
                </thead>
                <tbody>
                  {salaryRows.map((row, idx) => (
                    <tr key={row.id} className={`hover:bg-slate-50/80 ${row._delete ? "opacity-50 bg-rose-50/50" : ""}`}>
                      <td className="font-bold text-slate-950">
                        {row._editing ? (
                          <input
                            type="text"
                            value={row.name}
                            onChange={(e) => setSalaryRows(salaryRows.map((r, i) => i === idx ? { ...r, name: e.target.value.toUpperCase() } : r))}
                            placeholder="Employee name"
                            className="lss-input w-full px-2.5 py-1 text-xs uppercase"
                          />
                        ) : (
                          <span className={row._delete ? "line-through" : ""}>{row.name}</span>
                        )}
                      </td>
                      <td>
                        {row._editing ? (
                          <input
                            type="text"
                            value={row.emp_code || ""}
                            onChange={(e) => setSalaryRows(salaryRows.map((r, i) => i === idx ? { ...r, emp_code: e.target.value } : r))}
                            placeholder="E001"
                            className="lss-input w-full px-2.5 py-1 text-xs"
                          />
                        ) : (
                          <span className="text-xs font-semibold text-slate-700">{row.emp_code || "—"}</span>
                        )}
                      </td>
                      <td className="text-slate-500 font-medium">
                        {row._editing ? (
                          <input
                            type="text"
                            value={row.department}
                            onChange={(e) => setSalaryRows(salaryRows.map((r, i) => i === idx ? { ...r, department: e.target.value.toUpperCase() } : r))}
                            placeholder="STYLIST"
                            className="lss-input w-full px-2.5 py-1 text-xs uppercase"
                          />
                        ) : (
                          row.department
                        )}
                      </td>
                      <td className="text-right">
                        <input
                          type="number"
                          value={row.base_salary}
                          disabled={row._delete}
                          onChange={(e) => {
                            const val = Number(e.target.value) || 0;
                            setSalaryRows(salaryRows.map((r, i) => i === idx ? { ...r, base_salary: val } : r));
                          }}
                          className="lss-input w-32 text-right px-3 py-1 text-xs tabular disabled:opacity-50"
                        />
                      </td>
                      <td>
                        <input
                          type="date"
                          value={row.hire_date || ""}
                          disabled={row._delete}
                          onChange={(e) => setSalaryRows(salaryRows.map((r, i) => i === idx ? { ...r, hire_date: e.target.value } : r))}
                          className="lss-input px-2 py-1 text-xs tabular disabled:opacity-50"
                        />
                      </td>
                      <td>
                        <input
                          type="date"
                          value={row.exit_date || ""}
                          disabled={row._delete}
                          onChange={(e) => setSalaryRows(salaryRows.map((r, i) => i === idx ? { ...r, exit_date: e.target.value } : r))}
                          className="lss-input px-2 py-1 text-xs tabular disabled:opacity-50"
                        />
                      </td>
                      <td className="text-center">
                        <div className="flex items-center justify-center gap-1">
                          <button
                            onClick={() => setSalaryRows(salaryRows.map((r, i) => i === idx ? { ...r, _editing: !r._editing } : r))}
                            className={`p-1.5 rounded-lg transition ${row._editing ? "text-emerald-700 hover:bg-emerald-50" : "text-amber-700 hover:text-amber-900 hover:bg-amber-100"}`}
                            title={row._editing ? "Done editing" : "Edit employee"}
                          >
                            {row._editing ? <CheckCircle className="w-4 h-4" /> : <Edit2 className="w-4 h-4" />}
                          </button>
                          <button
                            onClick={() => setSalaryRows(salaryRows.map((r, i) => i === idx ? { ...r, _delete: !r._delete } : r))}
                            className={`p-1.5 rounded-lg transition ${row._delete ? "text-slate-500 hover:bg-slate-100" : "text-rose-600 hover:text-rose-900 hover:bg-rose-100"}`}
                            title={row._delete ? "Undo delete" : "Delete employee"}
                          >
                            {row._delete ? <Undo2 className="w-4 h-4" /> : <Trash2 className="w-4 h-4" />}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="border border-amber-200 rounded-xl bg-amber-50/50 p-4 space-y-3">
              <h4 className="text-xs font-extrabold uppercase tracking-wider text-slate-700">
                Record Salary Advance — deducts in {month}, posts to Cash Book as pending
              </h4>
              <form onSubmit={handleSaveAdvance} className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                <select value={advStaffId} onChange={(e) => setAdvStaffId(e.target.value)} className="lss-input text-xs font-semibold" required>
                  <option value="">Select staff…</option>
                  {salaryRows.filter((r) => !r._delete && !String(r.id || "").startsWith("new_")).map((r) => (
                    <option key={r.id} value={r.id}>{r.name}</option>
                  ))}
                </select>
                <input type="number" min="1" step="any" value={advAmount} onChange={(e) => setAdvAmount(e.target.value)} placeholder="Amount ₹" className="lss-input text-xs font-bold tabular" required />
                <input type="date" value={advDate} onChange={(e) => setAdvDate(e.target.value)} className="lss-input text-xs" required />
                <button type="submit" disabled={advSaving} className="lss-btn-gold px-3 py-2 text-xs font-bold uppercase tracking-wider disabled:opacity-50">
                  {advSaving ? "Saving…" : "Record Advance"}
                </button>
              </form>
              {advList.length > 0 && (
                <div className="space-y-1">
                  {advList.map((a) => (
                    <div key={a.id} className="flex items-center justify-between gap-2 text-xs font-semibold text-slate-700 bg-white border border-slate-200 rounded-lg px-3 py-1.5">
                      <span>{a.staff_name} · ₹{(a.amount || 0).toLocaleString()} · {a.date_given}</span>
                      <button onClick={() => handleDeleteAdvance(a.id)} className="text-rose-600 hover:text-rose-900 font-bold" title="Delete advance record">✕</button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="flex justify-end gap-3 pt-2">
              <button onClick={() => setShowSalaryModal(false)} className="lss-btn-outline px-4 py-2 text-sm">
                Cancel
              </button>
              <button
                onClick={handleSaveAllSalaries}
                disabled={salarySaving}
                className="lss-btn-gold px-5 py-2 text-sm disabled:opacity-50"
              >
                {salarySaving ? "Saving..." : "Save Salary Master"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Roster Window — daily grid: staff x ONE date (owner moved the master
          roster to a day-by-day cadence on 19 Oct 2026) */}
      {showRosterModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/60 backdrop-blur-xs" onClick={() => setShowRosterModal(false)}>
          <div className="bg-white border border-slate-200 rounded-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="px-6 py-4 border-b border-slate-200 flex flex-wrap items-center justify-between gap-3 bg-slate-50/70">
              <div>
                <h3 className="text-base font-extrabold text-slate-950 flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-emerald-700" />
                  Daily Roster — {prettyDate(rosterDate)}
                </h3>
                <p className="text-xs text-slate-500 mt-1 font-medium">
                  One day at a time: mark each staff Working (with shift) or Weekly Off for this date. Drives late rules, OT cut-off &amp; paid days.
                </p>
              </div>
              <button onClick={() => setShowRosterModal(false)} className="text-slate-400 hover:text-slate-700 transition">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="px-6 py-3 border-b border-slate-200 flex flex-wrap items-center gap-2 bg-white">
              <button onClick={() => setRosterDate(addDays(rosterDate, -1))} className="lss-btn-outline px-3 py-1.5 text-xs">
                ← Prev Day
              </button>
              <span className="text-xs font-bold text-slate-800 tabular">{prettyDate(rosterDate)}</span>
              <button onClick={() => setRosterDate(addDays(rosterDate, 1))} className="lss-btn-outline px-3 py-1.5 text-xs">
                Next Day →
              </button>
              <button onClick={() => setRosterDate(todayIST())} className="lss-btn-outline px-3 py-1.5 text-xs">
                Today
              </button>
              <button
                onClick={handleFillPrevDay}
                disabled={rosterSaving}
                title="Paint this day with yesterday's saved roster — review, then Save"
                className="lss-btn-gold px-3 py-1.5 text-xs disabled:opacity-50 ml-auto"
              >
                Copy Previous Day
              </button>
            </div>

            <div className="p-6 overflow-auto flex-1">
              {rosterLoading ? (
                <div className="p-12 text-center text-slate-500 font-medium">Loading roster...</div>
              ) : (
                <div className="border border-slate-200 rounded-xl overflow-x-auto">
                  <table className="lss-table min-w-[520px]">
                    <thead>
                      <tr>
                        <th className="min-w-[180px]">Staff</th>
                        <th className="text-center">This Day</th>
                        <th className="text-center">Shift</th>
                      </tr>
                    </thead>
                    <tbody>
                      {staffList.map((s) => {
                        const r = daySlice(s.id);
                        const setR = (patch) => setDaySlice(s.id, patch);
                        return (
                          <tr key={s.id} className="hover:bg-slate-50/80">
                            <td className="font-bold text-slate-950 text-xs">
                              {s.name}
                              <div className="text-[10px] text-slate-500 font-semibold uppercase">{s.role || s.department || ""}</div>
                            </td>
                            <td className="text-center">
                              <select
                                value={r.day}
                                onChange={(e) => {
                                  const v = e.target.value;
                                  // Picking "Working" pre-fills the morning shift so a
                                  // row can never be left working-with-no-shift.
                                  setR({ day: v, shift: v === "off" ? "" : r.shift || ROSTER_SHIFTS[0] });
                                }}
                                className={`w-full max-w-[200px] text-xs font-bold px-3 py-2 rounded-lg border focus:outline-none focus:ring-2 focus:ring-amber-400/30 focus:border-amber-400 transition cursor-pointer ${
                                  r.day === "off"
                                    ? "bg-rose-50 text-rose-900 border-rose-300"
                                    : r.day === "work"
                                    ? "bg-emerald-50 text-emerald-900 border-emerald-300"
                                    : "bg-white text-slate-400 border-slate-200"
                                }`}
                              >
                                <option value="">Not set</option>
                                <option value="work">Working</option>
                                <option value="off">Weekly Off</option>
                              </select>
                            </td>
                            <td className="text-center">
                              <select
                                value={r.shift}
                                disabled={r.day !== "work"}
                                onChange={(e) => setR({ shift: e.target.value })}
                                className={`w-full max-w-[200px] text-xs font-bold px-3 py-2 rounded-lg border focus:outline-none focus:ring-2 focus:ring-amber-400/30 focus:border-amber-400 transition cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed ${
                                  r.shift === "Evening (11-9)"
                                    ? "bg-sky-50 text-sky-950 border-sky-300"
                                    : r.shift
                                    ? "bg-emerald-50 text-emerald-900 border-emerald-300"
                                    : "bg-white text-slate-400 border-slate-200"
                                }`}
                              >
                                <option value="">{r.day === "off" ? "—" : "Select shift..."}</option>
                                {ROSTER_SHIFTS.map((sh) => (
                                  <option key={sh} value={sh}>{sh}</option>
                                ))}
                              </select>
                            </td>
                          </tr>
                        );
                      })}
                      {staffList.length === 0 && (
                        <tr>
                          <td colSpan={3} className="text-center text-slate-500 font-medium py-8 text-xs">
                            No staff found.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            <div className="px-6 py-4 border-t border-slate-200 flex justify-end gap-3 bg-slate-50/70">
              <button onClick={() => setShowRosterModal(false)} className="lss-btn-outline px-4 py-2 text-sm">
                Close
              </button>
              <button
                onClick={handleSaveRoster}
                disabled={rosterSaving}
                title={`Writes only ${rosterDate}`}
                className="lss-btn-gold px-5 py-2 text-sm disabled:opacity-50"
              >
                {rosterSaving ? "Saving..." : `Save ${prettyDate(rosterDate)} ✓`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Leave Requests + Proof Attachment + Approval */}
      {showLeaveModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/60 backdrop-blur-xs" onClick={() => setShowLeaveModal(false)}>
          <div className="bg-white border border-slate-200 rounded-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between bg-slate-50/70">
              <div>
                <h3 className="text-base font-extrabold text-slate-950 flex items-center gap-2">
                  <FileText className="w-5 h-5 text-sky-800" />
                  Leave Requests & Proof Approval
                  {pendingLeaves > 0 && (
                    <span className="text-[10px] font-extrabold bg-rose-100 text-rose-900 border border-rose-300 px-2 py-0.5 rounded-full">
                      {pendingLeaves} Pending
                    </span>
                  )}
                </h3>
                <p className="text-xs text-slate-500 mt-1 font-medium">
                  Approved leaves are knowledge-only (absent stays unpaid) but the peak-day penalty is removed. Attach proof for weekend approvals.
                </p>
              </div>
              <button onClick={() => setShowLeaveModal(false)} className="text-slate-400 hover:text-slate-700 transition">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 overflow-y-auto space-y-6 flex-1">
              {pendingLeaves > 0 && (
                <div className="flex items-center gap-3 bg-rose-50 border border-rose-300 rounded-xl px-4 py-3">
                  <span className="bg-rose-600 text-white text-sm font-extrabold min-w-8 h-8 px-2 rounded-full flex items-center justify-center">
                    {pendingLeaves}
                  </span>
                  <div>
                    <div className="text-sm font-extrabold text-rose-900">
                      {pendingLeaves} leave request{pendingLeaves > 1 ? "s" : ""} awaiting approval
                    </div>
                    <div className="text-xs text-rose-700 font-medium">Review proof and Approve / Reject below.</div>
                  </div>
                </div>
              )}
              {/* New request form */}
              <form onSubmit={handleSaveLeave} className="bg-slate-50 border border-slate-200 rounded-xl p-4">
                <h4 className="text-xs font-bold uppercase tracking-wider text-slate-700 mb-3">New Leave Request</h4>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                  <div>
                    <label className="block text-xs font-bold text-slate-600 mb-1">Staff</label>
                    <select
                      value={leaveForm.staff_id}
                      onChange={(e) => setLeaveForm({ ...leaveForm, staff_id: e.target.value })}
                      className="lss-input w-full px-3 py-2 text-xs"
                      required
                    >
                      <option value="">Select staff...</option>
                      {staffList.map((s) => (
                        <option key={s.id} value={s.id}>{s.name}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-slate-600 mb-1">Date</label>
                    <input
                      type="date"
                      value={leaveForm.date}
                      onChange={(e) => setLeaveForm({ ...leaveForm, date: e.target.value })}
                      className="lss-input w-full px-3 py-2 text-xs"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-slate-600 mb-1">Reason</label>
                    <input
                      type="text"
                      value={leaveForm.reason}
                      onChange={(e) => setLeaveForm({ ...leaveForm, reason: e.target.value })}
                      placeholder="Sick / family / ..."
                      className="lss-input w-full px-3 py-2 text-xs"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-slate-600 mb-1">Proof (≤2 MB)</label>
                    <input type="file" ref={proofInputRef} onChange={handleProofFile} accept="image/*,.pdf" className="hidden" />
                    <button
                      type="button"
                      onClick={() => proofInputRef.current?.click()}
                      className={`w-full px-3 py-2 text-xs font-bold rounded-[10px] border transition ${
                        leaveForm.proof_url
                          ? "bg-emerald-50 text-emerald-900 border-emerald-300"
                          : "bg-white text-slate-600 border-slate-300 hover:border-amber-400"
                      }`}
                    >
                      {leaveForm.proof_name ? `✓ ${leaveForm.proof_name.slice(0, 18)}` : "Attach Proof"}
                    </button>
                  </div>
                </div>
                <div className="flex justify-end mt-3">
                  <button type="submit" disabled={leaveSaving} className="lss-btn-gold px-5 py-2 text-xs disabled:opacity-50">
                    {leaveSaving ? "Submitting..." : "Submit Request ✓"}
                  </button>
                </div>
              </form>

              {/* Requests list */}
              <div>
                <h4 className="text-xs font-bold uppercase tracking-wider text-slate-700 mb-3">
                  Requests — {month} ({monthLeaves.length})
                </h4>
                {leaveLoading ? (
                  <div className="p-8 text-center text-slate-500 font-medium text-xs">Loading requests...</div>
                ) : monthLeaves.length === 0 ? (
                  <div className="p-8 text-center text-slate-500 font-medium text-xs">
                    No leave requests for {month}.
                  </div>
                ) : (
                  <div className="border border-slate-200 rounded-xl overflow-x-auto">
                    <table className="lss-table min-w-[700px]">
                      <thead>
                        <tr>
                          <th>Staff</th>
                          <th>Date</th>
                          <th>Reason</th>
                          <th className="text-center">Proof</th>
                          <th className="text-center">Status</th>
                          <th className="text-center">Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {monthLeaves.map((l) => (
                          <tr key={l.id} className="hover:bg-slate-50/80">
                            <td className="font-bold text-slate-950 text-xs">{l.staff_name}</td>
                            <td className="tabular text-xs font-semibold">{l.date}</td>
                            <td className="text-xs text-slate-600">{l.reason || "—"}</td>
                            <td className="text-center">
                              {l.proof_url ? (
                                <a
                                  href={l.proof_url}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="text-[11px] font-bold text-sky-800 hover:text-sky-950 underline"
                                >
                                  View Proof
                                </a>
                              ) : (
                                <span className="text-slate-300 text-xs">—</span>
                              )}
                            </td>
                            <td className="text-center">
                              {l.status === "approved" && <span className="lss-badge text-[10px] bg-emerald-100 text-emerald-900 border-emerald-300">Approved</span>}
                              {l.status === "rejected" && <span className="lss-badge text-[10px] bg-rose-100 text-rose-900 border-rose-300">Rejected</span>}
                              {l.status === "pending" && <span className="lss-badge text-[10px] bg-amber-100 text-amber-950 border-amber-300">Pending</span>}
                            </td>
                            <td className="text-center">
                              {l.status === "pending" ? (
                                isOwner ? (
                                  <div className="flex items-center justify-center gap-1.5">
                                    <button
                                      onClick={() => handleLeaveAction(l.id, "approved")}
                                      className="px-2.5 py-1 text-[11px] font-bold rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white transition"
                                    >
                                      Approve
                                    </button>
                                    <button
                                      onClick={() => handleLeaveAction(l.id, "rejected")}
                                      className="px-2.5 py-1 text-[11px] font-bold rounded-lg bg-white hover:bg-rose-50 text-rose-700 border border-rose-300 transition"
                                    >
                                      Reject
                                    </button>
                                  </div>
                                ) : (
                                  <span className="text-[11px] text-slate-400 font-medium">Awaiting owner</span>
                                )
                              ) : (
                                <span className="text-[11px] text-slate-400 font-medium">by {l.approved_by || "—"}</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
              {/* Swap approvals live here too (not in the swap window) */}
              <div>
                <h4 className="text-xs font-bold uppercase tracking-wider text-slate-700 mb-3 flex items-center gap-2">
                  Swap Requests ({swapList.length})
                  {pendingSwaps > 0 && (
                    <span className="text-[10px] font-extrabold bg-rose-100 text-rose-900 border border-rose-300 px-2 py-0.5 rounded-full">
                      {pendingSwaps} Pending
                    </span>
                  )}
                </h4>
                {swapList.length === 0 ? (
                  <div className="p-8 text-center text-slate-500 font-medium text-xs border border-dashed border-slate-300 rounded-xl">
                    No swap requests this month.
                  </div>
                ) : (
                  <div className="border border-slate-200 rounded-xl overflow-x-auto">
                    <table className="lss-table min-w-[700px]">
                      <thead>
                        <tr>
                          <th>Swap</th>
                          <th>Date</th>
                          <th className="text-center">Proof</th>
                          <th className="text-center">Status</th>
                          <th className="text-center">Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {swapList.map((s) => {
                          const st = s.status || "pending";
                          return (
                            <tr key={s.id} className="hover:bg-slate-50/80">
                              <td className="font-bold text-slate-950 text-xs">{s.staff_a_name} → {s.staff_b_name}</td>
                              <td className="tabular text-xs font-semibold">{s.date}</td>
                              <td className="text-center">
                                {s.proof_url ? (
                                  <a href={s.proof_url} target="_blank" rel="noreferrer" className="text-[11px] font-bold text-sky-800 hover:text-sky-950 underline">
                                    View Proof
                                  </a>
                                ) : (
                                  <span className="text-slate-300 text-xs">—</span>
                                )}
                              </td>
                              <td className="text-center">
                                {st === "approved" && <span className="lss-badge text-[10px] bg-emerald-100 text-emerald-900 border-emerald-300">Approved</span>}
                                {st === "rejected" && <span className="lss-badge text-[10px] bg-rose-100 text-rose-900 border-rose-300">Rejected</span>}
                                {st === "pending" && <span className="lss-badge text-[10px] bg-amber-100 text-amber-950 border-amber-300">Pending</span>}
                              </td>
                              <td className="text-center">
                                {st === "pending" ? (
                                  isOwner ? (
                                    <div className="flex items-center justify-center gap-1.5">
                                      <button
                                        onClick={() => handleSwapAction(s.id, "approved")}
                                        className="px-2.5 py-1 text-[11px] font-bold rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white transition"
                                      >
                                        Approve
                                      </button>
                                      <button
                                        onClick={() => handleSwapAction(s.id, "rejected")}
                                        className="px-2.5 py-1 text-[11px] font-bold rounded-lg bg-white hover:bg-rose-50 text-rose-700 border border-rose-300 transition"
                                      >
                                        Reject
                                      </button>
                                    </div>
                                  ) : (
                                    <span className="text-[11px] text-slate-400 font-medium">Awaiting owner</span>
                                  )
                                ) : (
                                  <span className="text-[11px] text-slate-400 font-medium">by {s.approved_by || "—"}</span>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>

            <div className="px-6 py-4 border-t border-slate-200 flex justify-end bg-slate-50/70">
              <button onClick={() => setShowLeaveModal(false)} className="lss-btn-outline px-4 py-2 text-sm">
                Close
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
